import { useEffect, useState, useCallback } from "react";
import { http, formatApiError, formatINR, formatPct } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import TickerSearch from "../components/TickerSearch";
import CSVImportDialog from "../components/CSVImportDialog";

const EXCHANGES = ["NSE", "BSE", "US", "CRYPTO", "OTHER"];
const TYPES = ["Stock", "ETF", "Mutual Fund", "Crypto", "Bond", "Other"];
const EMPTY = { symbol: "", name: "", quantity: "", avg_price: "", exchange: "NSE", asset_type: "Stock", notes: "" };

export default function Portfolio() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/portfolio/holdings");
      setItems(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const payload = {
      symbol: form.symbol.toUpperCase().trim(),
      name: form.name || null,
      quantity: parseFloat(form.quantity),
      avg_price: parseFloat(form.avg_price),
      exchange: form.exchange, asset_type: form.asset_type,
      notes: form.notes || null,
    };
    if (!payload.symbol || !payload.quantity || !payload.avg_price) {
      toast.error("Symbol, quantity, and average price are required.");
      return;
    }
    try {
      if (editing) {
        await http.put(`/portfolio/holdings/${editing}`, payload);
        toast.success("Holding updated");
      } else {
        await http.post("/portfolio/holdings", payload);
        toast.success("Holding added");
      }
      setOpen(false); setEditing(null); setForm(EMPTY);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this holding?")) return;
    try {
      await http.delete(`/portfolio/holdings/${id}`);
      toast.success("Deleted");
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openEdit = (h) => {
    setEditing(h.id);
    setForm({ symbol: h.symbol, name: h.name || "", quantity: String(h.quantity),
      avg_price: String(h.avg_price), exchange: h.exchange, asset_type: h.asset_type, notes: h.notes || "" });
    setOpen(true);
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-500 font-bold">Investments</div>
          <h1 className="mt-2 font-display text-4xl font-light">Portfolio</h1>
          <p className="text-zinc-400 mt-2 text-sm">Manual holdings + live prices (NSE symbols auto-append <span className="font-mono">.NS</span>).</p>
        </div>
        <div className="flex gap-2">
          <CSVImportDialog onImported={load} />
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-holding-button">
              <Plus size={16} className="mr-2" /> Add holding
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} holding</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Symbol</Label>
                <div className="mt-1">
                  <TickerSearch
                    value={form.symbol}
                    onChange={(v) => setForm((f) => ({ ...f, symbol: v }))}
                    onSelect={(r) => setForm((f) => ({
                      ...f, symbol: r.symbol,
                      name: r.name || f.name,
                      exchange: r.exchange || f.exchange,
                      asset_type: r.type === "ETF" ? "ETF" : (r.type === "MUTUALFUND" ? "Mutual Fund" : f.asset_type),
                    }))}
                    testId="holding-symbol-input"
                  />
                </div>
              </div>
              <div>
                <Label>Exchange</Label>
                <Select value={form.exchange} onValueChange={(v) => setForm({ ...form, exchange: v })}>
                  <SelectTrigger data-testid="holding-exchange-select" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {EXCHANGES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Quantity</Label>
                <Input data-testid="holding-qty-input" type="number" step="any" value={form.quantity}
                  onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label>Avg buy price (₹)</Label>
                <Input data-testid="holding-price-input" type="number" step="any" value={form.avg_price}
                  onChange={(e) => setForm({ ...form, avg_price: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-800" />
              </div>
              <div className="col-span-2">
                <Label>Asset type</Label>
                <Select value={form.asset_type} onValueChange={(v) => setForm({ ...form, asset_type: v })}>
                  <SelectTrigger data-testid="holding-type-select" className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {TYPES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Notes (optional)</Label>
                <Input data-testid="holding-notes-input" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-800" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={submit} data-testid="holding-save-button">
                {editing ? "Save" : "Add holding"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 text-[11px] uppercase tracking-[0.15em] text-zinc-500 border-b border-zinc-800 bg-zinc-950/50">
          <div className="col-span-3">Symbol</div>
          <div className="col-span-1">Exch</div>
          <div className="col-span-2 text-right">Qty × Avg</div>
          <div className="col-span-2 text-right">Current</div>
          <div className="col-span-2 text-right">Market value</div>
          <div className="col-span-1 text-right">P&L</div>
          <div className="col-span-1"></div>
        </div>
        {loading ? (
          <div className="p-6 text-zinc-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-zinc-500">
            No holdings yet. Click <span className="text-emerald-400">Add holding</span> to start.
          </div>
        ) : items.map((h) => (
          <div key={h.id} className="grid grid-cols-12 px-6 py-4 items-center border-b border-zinc-800 last:border-0 hover:bg-zinc-800/40"
            data-testid={`holding-row-${h.symbol}`}>
            <div className="col-span-3">
              <div className="font-medium">{h.symbol}</div>
              <div className="text-xs text-zinc-500">{h.name || h.asset_type}</div>
            </div>
            <div className="col-span-1 text-xs text-zinc-400 font-mono">{h.exchange}</div>
            <div className="col-span-2 text-right font-mono text-sm text-zinc-300">
              {h.quantity} × {formatINR(h.avg_price, { decimals: 2 })}
            </div>
            <div className="col-span-2 text-right font-mono text-sm">{formatINR(h.current_price)}</div>
            <div className="col-span-2 text-right font-mono">{formatINR(h.market_value)}</div>
            <div className={`col-span-1 text-right font-mono text-sm ${h.pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {formatPct(h.pnl_pct)}
              <div className="text-xs opacity-70">{formatINR(h.pnl, { sign: true, decimals: 0 })}</div>
            </div>
            <div className="col-span-1 flex justify-end gap-1">
              <Button size="icon" variant="ghost" className="text-zinc-400 hover:text-zinc-100"
                onClick={() => openEdit(h)} data-testid={`edit-holding-${h.symbol}`}>
                <PencilSimple size={16} />
              </Button>
              <Button size="icon" variant="ghost" className="text-red-400 hover:text-red-300"
                onClick={() => del(h.id)} data-testid={`delete-holding-${h.symbol}`}>
                <Trash size={16} />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
